import { Activity } from 'lucide-react';
import { DevToolsButton } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsButton';
import { DevToolsEmptyState } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsEmptyState';
import { EventsChannelFilter } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsChannelFilter';
import type { EventChannel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsChannelFilter/index.types';
import { EventsFilteredEmpty } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsFilteredEmpty';
import { EventsItem } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsItem';
import { EventsLegend } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsLegend';
import { EventsLegFilter } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsLegFilter';
import {
  CHANNEL_LABELS,
  UNATTRIBUTED,
} from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/index.constants';
import {
  SURFACE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import {
  filterEventsByLeg,
  getEventLegs,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import {
  EventService,
  type SoftphoneEvent,
} from 'Modules/softphone/services/EventService';
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '~/lib/utils';

const emptyCounts = (): Record<SoftphoneEvent['type'], number> => ({
  SIP: 0,
  RTP: 0,
  DEVICE: 0,
  CALL: 0,
});

/**
 * EventsTab - What happened, in order, on one clock
 *
 * Two filters, and they are not the same kind of thing.
 */
export const EventsTab: React.FC = () => {
  const [events, setEvents] = useState<SoftphoneEvent[]>([]);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedLeg, setSelectedLeg] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<EventChannel>(null);
  const eventsEndRef = useRef<HTMLDivElement>(null);
  const eventService = EventService.getInstance();

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (!isPaused && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, isPaused]);

  // Subscribe to real-time events
  useEffect(() => {
    // Load existing events
    setEvents(eventService.getAll());

    // Subscribe to new events
    const unsubscribe = eventService.subscribe((newEvent) => {
      if (!isPaused) {
        setEvents((prev) => [...prev, newEvent]);
      }
    });

    return unsubscribe;
  }, [eventService, isPaused]);

  const handleClearEvents = () => {
    eventService.clear();
    setEvents([]);
    setExpandedEventId(null);
  };

  const handleToggleExpand = (eventId: string) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  const legs = getEventLegs(events);
  const legEvents = filterEventsByLeg(events, selectedLeg);

  // Counts follow the leg filter, so the channel chips describe what is
  // actually on screen rather than the whole buffer.
  const countsByChannel = legEvents.reduce((counts, event) => {
    counts[event.type] += 1;

    return counts;
  }, emptyCounts());

  const visibleEvents = selectedChannel
    ? legEvents.filter((event) => event.type === selectedChannel)
    : legEvents;

  // Named exactly as the chips are, so the sentence points at something the
  // reader can see and switch off.
  const activeFilters = [
    selectedChannel ? CHANNEL_LABELS[selectedChannel] : null,
    selectedLeg === UNATTRIBUTED ? 'no call' : selectedLeg,
  ].filter((label): label is string => Boolean(label));

  const clearFilters = () => {
    setSelectedChannel(null);
    setSelectedLeg(null);
  };

  return (
    <div className="flex h-full flex-col gap-2.5">
      <div className="flex flex-shrink-0 items-center gap-2">
        <h3 className="text-[14px] font-semibold text-zinc-200">Timeline</h3>
        <span className="font-sp-mono text-[11px] text-zinc-400">
          {activeFilters.length > 0
            ? `${events.length} in buffer · ${visibleEvents.length} shown`
            : `${events.length} events · one clock`}
        </span>
        <div className="flex-1" />
        <DevToolsButton
          onClick={() => setIsPaused(!isPaused)}
          className={cn(
            'bg-zinc-900',
            isPaused && 'border-amber-800 text-amber-400 hover:text-amber-400'
          )}
        >
          {isPaused ? 'Resume' : 'Pause'}
        </DevToolsButton>
        {events.length > 0 && (
          <DevToolsButton variant="destructive" onClick={handleClearEvents}>
            Clear
          </DevToolsButton>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col gap-1.5">
        <EventsChannelFilter
          selected={selectedChannel}
          onSelect={setSelectedChannel}
          countsByChannel={countsByChannel}
          total={legEvents.length}
        />

        {/* Per-leg filter — only worth showing once more than one leg exists */}
        {legs.length > 1 && (
          <EventsLegFilter
            legs={legs}
            selectedLeg={selectedLeg}
            onSelectLeg={setSelectedLeg}
          />
        )}
      </div>

      {events.length > 0 ? (
        // No `flex-1`: the card takes its height from the timeline and stops
        // there. Two events should not stretch a card down the whole panel —
        // the empty space below it is the honest signal that little happened.
        // Once the list outgrows the space the card shrinks instead and the
        // list scrolls inside it, with the buffer footer staying put.
        <div className={cn('flex min-h-0 flex-col', SURFACE.card)}>
          <div className="min-h-0 overflow-y-auto">
            {visibleEvents.length > 0 ? (
              <>
                {visibleEvents.map((event) => (
                  <EventsItem
                    key={event.id}
                    event={event}
                    isExpanded={expandedEventId === event.id}
                    onToggle={() => handleToggleExpand(event.id)}
                  />
                ))}
                <div ref={eventsEndRef} />
              </>
            ) : (
              <EventsFilteredEmpty
                activeFilters={activeFilters}
                hiddenCount={events.length}
                onShowAll={clearFilters}
              />
            )}
          </div>
          <div className="flex-shrink-0">
            <EventsLegend
              bufferCount={events.length}
              bufferMax={eventService.getMaxEvents()}
            />
          </div>
        </div>
      ) : (
        <div className="min-h-0 overflow-y-auto">
          <DevToolsEmptyState
            icon={<Activity className="h-[21px] w-[21px]" strokeWidth={1.7} />}
            title="No events captured"
            body="Every SIP message on the wire, RTP stream and ICE state, call lifecycle changes, and device switches all land here on one shared clock, newest at the bottom."
            chips={['SIP', 'RTP', 'CALL', 'DEV']}
            hint={`${isPaused ? 'capture paused' : 'capturing'} · buffer 0/${eventService.getMaxEvents()}`}
          />
        </div>
      )}
    </div>
  );
};
